import { useCallback, useEffect, useState, useMemo } from 'react';
import { MonacoDiffEditor } from 'react-monaco-editor';
import PropTypes from 'prop-types';
import withTheme from '@mui/styles/withTheme';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import { useMessage } from 'studio/components/Message';
import Progress from 'studio/components/Progress';

import services from 'services';

import IconClose from 'svg/v2/close.svg';

import { titleMarginDragWidthInPixels, useStyles } from './CompareFilesModal.styles';

import { trackEvent } from 'utils/eventTracking';

const numberToStringPixelValue = value => `${value}px`;

const pathToCaption = path => path.replaceAll('/', ' > ');

const TitleContainer = ({ title, width, kgId, relativeFilePath }) => {
  const styles = useStyles();

  return (
    <div className={styles.titleContainer} style={{ width }}>
      <span className={styles.title}>{title}</span>
      <br />
      <span className={styles.captionKgId}>{kgId}</span>
      <span className={styles.caption}>{pathToCaption(`/${relativeFilePath}`)}</span>
    </div>
  );
};

const initialState = {
  knowledgeGraph: undefined,
  filePath: undefined,
  originalFileContent: undefined,
  isFetchingOriginalFileContent: false,
  hasFetchedOriginalFileContent: false,
  errorFetchingOriginalFileContent: false,
  errorMessageText: '',
  currentFileContent: undefined
};

const CompareFilesModal = ({ theme, open, knowledgeGraph, filePath, currentFileContent, onClose }) => {
  const styles = useStyles();
  const { errorMessage } = useMessage();

  const [state, setState] = useState(initialState);
  const [editorTitleWidth, setEditorTitleWidth] = useState({ original: '50%', modified: '50%' });

  const originalKgId = useMemo(() => knowledgeGraph?.developmentKgForId, [knowledgeGraph?.developmentKgForId]);

  // The first index of the path is the KG id. By removing the index, we
  // get a "generic" file path, meaning it is the same for the branch KG and the main KG.
  const relativeFilePath = useMemo(() => {
    if (!filePath) return '';
    return filePath.split('/').slice(1).join('/');
  }, [filePath]);

  const showErrorMessage = message => {
    setState(prev => ({
      ...prev,
      errorMessageText: message,
      isFetchingOriginalFileContent: false,
      errorFetchingOriginalFileContent: true
    }));
    errorMessage(message);
  };

  // The title/caption boxes are independent of the editor. So if we resize the editor by dragging
  // the column in the middle, the titles width has to be adjusted accordingly.
  const resizeEditorsTitles = widthInEditor => {
    const widthInTitle = widthInEditor - Math.floor(titleMarginDragWidthInPixels / 2);

    setEditorTitleWidth(prev => ({
      ...prev,
      original: numberToStringPixelValue(widthInTitle),
      modified: `calc(100% - ${numberToStringPixelValue(widthInTitle)})`
    }));
  };

  const editorDidMount = async editor => {
    const originalEditor = editor.getOriginalEditor();

    // As soon as we render the editors, we get the width of the "original" editor.
    // Based on it, we calculate the width of the title containers for the original and the modified editors.
    const layoutInfo = originalEditor.getLayoutInfo();
    if (layoutInfo.width !== editorTitleWidth.original) {
      resizeEditorsTitles(layoutInfo.width);
    }

    // This is an observer (just like "componentDidUpdate" from React), which observes whether the
    // editors are resized. If they are, we resize the title/caption boxes as well.
    originalEditor.onDidLayoutChange(newLayoutInfo => {
      if (numberToStringPixelValue(newLayoutInfo.width) !== editorTitleWidth.original) {
        resizeEditorsTitles(newLayoutInfo.width);
      }
    });
  };

  // By default, material-UI's Dialog is always rendered on the DOM.
  // So we have to reset the state when closing it.
  const handleClose = useCallback(() => {
    setState(initialState);
    onClose();
  }, [onClose]);

  const fetchOriginalFileContent = useCallback(async () => {
    setState(prev => ({ ...prev, errorMessageText: '' }));

    const originalFileTreeResult = await services.getKnowledgeGraphFiles(originalKgId);
    if (!originalFileTreeResult.success) {
      return showErrorMessage(
        originalFileTreeResult.response?.data?.data?.message || 'Error fetching original file tree.'
      );
    }

    const originalFile = originalFileTreeResult.data.data.find(
      fileData => fileData.data.path === originalKgId + '/' + relativeFilePath
    )?.data;
    if (!originalFile) {
      return showErrorMessage('Could not find original file inside file tree.');
    }

    const originalFileContent = await services.getKnowledgeGraphFileContent(originalKgId, originalFile.id);
    if (!originalFileContent.success) {
      return showErrorMessage(
        originalFileContent.response?.data?.data?.message || 'There was an error getting file content.'
      );
    }

    setState(prev => ({
      ...prev,
      isFetchingOriginalFileContent: false,
      hasFetchedOriginalFileContent: true,
      originalFileContent: originalFileContent.data.content
    }));
  }, [originalKgId, relativeFilePath]);

  // fills initial state after opening modal and making sure we have all current information
  useEffect(() => {
    if (open && knowledgeGraph && filePath && currentFileContent) {
      setState({ knowledgeGraph, filePath, currentFileContent });
    }
  }, [open, knowledgeGraph, filePath, currentFileContent]);

  useEffect(() => {
    if (
      open &&
      state.knowledgeGraph &&
      state.filePath &&
      state.currentFileContent &&
      !state.hasFetchedOriginalFileContent &&
      !state.isFetchingOriginalFileContent &&
      !state.errorFetchingOriginalFileContent
    ) {
      trackEvent('Compare Files Modal Opened', {
        knowledgeGraphId: state.knowledgeGraph.id,
        mainKnowledgeGraphId: knowledgeGraph?.developmentKgForId
      });
      fetchOriginalFileContent();
      setState(prev => ({ ...prev, isFetchingOriginalFileContent: true }));
    }
  }, [
    open,
    state.knowledgeGraph,
    state.filePath,
    state.currentFileContent,
    state.isFetchingOriginalFileContent,
    state.hasFetchedOriginalFileContent,
    state.errorFetchingOriginalFileContent,
    fetchOriginalFileContent
  ]);

  return (
    <Dialog classes={{ paper: styles.dialogPaper }} fullWidth maxWidth={false} open={open} onClose={handleClose}>
      <DialogTitle>
        <div className={styles.modalTitleContainer}>
          <span>Compare files</span>
          <IconClose className={styles.closeIcon} onClick={handleClose} />
        </div>
      </DialogTitle>
      <DialogContent classes={{ root: styles.dialogContentRoot }}>
        {open &&
          (state.isFetchingOriginalFileContent ? (
            <Progress />
          ) : state.errorFetchingOriginalFileContent ? (
            <div className={styles.errorContainer}>{state.errorMessageText}</div>
          ) : (
            state.currentFileContent &&
            state.originalFileContent && (
              <>
                <div className={styles.container}>
                  <div className={styles.editorTitles}>
                    <TitleContainer
                      title="Main Branch"
                      width={editorTitleWidth.original}
                      kgId={originalKgId}
                      relativeFilePath={relativeFilePath}
                    />
                    <div className={styles.titleMargin} />
                    <TitleContainer
                      title="Your Development Branch"
                      width={editorTitleWidth.modified}
                      kgId={knowledgeGraph.id}
                      relativeFilePath={relativeFilePath}
                    />
                  </div>
                  <div className={styles.editorComponent}>
                    <MonacoDiffEditor
                      theme={theme.palette.mode}
                      language="vkl"
                      options={{
                        scrollBeyondLastLine: false,
                        showUnused: false,
                        codeLens: true,
                        automaticLayout: true,
                        readOnly: true,
                        scrollbar: { verticalScrollbarSize: 6 },
                        glyphMargin: true,
                        lineNumbersMinChars: 3
                      }}
                      key={filePath}
                      onChange={() => {}}
                      editorDidMount={editorDidMount}
                      // The original code appears on the LEFT side and the modified appears on the RIGHT side.
                      // I wanted to switch viewers but there's no option to do it. Here's the (negative) answer
                      // from the creators when this feature was suggested:
                      // https://github.com/microsoft/monaco-editor/issues/368#issuecomment-1443457735
                      original={state.originalFileContent}
                      value={state.currentFileContent}
                    />
                  </div>
                </div>
              </>
            )
          ))}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose} {...(!theme.isDark ? { color: 'primary' } : '')}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTheme(CompareFilesModal);

CompareFilesModal.propTypes = {
  theme: PropTypes.object,
  open: PropTypes.bool,
  knowledgeGraph: PropTypes.object,
  filePath: PropTypes.string,
  currentFileContent: PropTypes.string,
  onClose: PropTypes.func
};
